'use client';

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PortalLink,
  useCurrentUser,
  useLearningSource,
  type CurrentCourse,
} from '@akademi/site-builder-runtime';
import { LastCourseCard, LastCourseCardSkeleton, LastCourseCardStyles } from './LastCourseCard';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import type { PaddingBox } from '../_shared/resolvePadding';
import { EmptyState } from '../_shared/EmptyState';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';

/** Unit the backend duration fields ({@code courseDuration},
 *  {@code attemptDuration}) arrive in. {@code 'auto'} guesses per value —
 *  see {@link toMinutes}. */
export type DurationUnit = 'auto' | 'minutes' | 'seconds' | 'milliseconds';

export interface LastCourseProps {
  title?: string;
  continueLabel?: string;
  courseHrefTemplate?: string;
  seeAllLabel?: string;
  seeAllHref?: string;
  showButton?: boolean;
  /** 'full' (default): the whole card is the link. 'button': only the
   *  continue button is clickable — the rest of the card is inert. */
  linkMode?: 'full' | 'button';
  hideWhenEmpty?: boolean;
  /** How many courses to show side-by-side in a row (1–2). Admin-managed.
   *  Arrives as a string from the admin select ("2"); coerced in {@link resolveColumns}. */
  columns?: 1 | 2 | '1' | '2';
  style?: CSSProperties;
  paddingOuter?: PaddingBox;
  /** Draw the decorative gradient rule beside the section heading. */
  headingLine?: boolean;
  /** Card palette. 'dark' (default) renders the near-black card of the
   *  reference design regardless of the host theme; 'auto' follows the
   *  host's design tokens (so it flips with the portal's light/dark
   *  theme); 'light' pins the token/light palette. */
  cardTheme?: 'dark' | 'auto' | 'light';
  /** Show the "Kalan Süre: 45dk" line under the progress bar when the
   *  backend reports a duration. Off → the footer holds only the CTA. */
  showRemainingTime?: boolean;
  /** Unit of the backend {@code courseDuration}/{@code attemptDuration}
   *  fields the remaining time is computed from. Both arrive in seconds
   *  on {@code /v1/employee/course/current/search}, which is the default. */
  durationUnit?: DurationUnit;
  /** Footer text shown INSTEAD of the remaining time when the learner
   *  finished the content but still owes the post-test — see
   *  {@link isPostTestPending}. Admin-managed and localized; falls back
   *  to {@code label.postTestRequired} from the translation catalog. */
  postTestLabel?: string;
  paddingInner?: PaddingBox;
  /** Design token size key e.g. "xl", "3xl". Maps to var(--font-size-{key}).
   *  Defaults to "xl" so the heading matches the compact profile-block
   *  family (ProfileCourses/Badges/Stats); pass "3xl" for a prominent
   *  hero-style section heading (the home "Kaldığın Yerden Devam Et" row). */
  titleFontSize?: string;
}

/** Clamp an admin-supplied column count into the supported 1–2 range.
 *  Stale saved values (3/4/5 from a previous config) cap at 2. */
function resolveColumns(columns: LastCourseProps['columns']): number {
  const n = Math.round(Number(columns) || 1);
  return Math.min(2, Math.max(1, n));
}

/**
 * Slugify a title into a URL-safe segment — mirrors
 * {@code learningItemToCardProps} so the "continue" card links resolve
 * to the same canonical detail URLs the course cards build.
 */
function slugifyTitle(title: string | undefined): string {
  return (title ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Read the first key that carries a usable value off a raw course row.
 *  {@code /api/current-course} forwards the backend row untouched, so
 *  fields beyond the {@link CurrentCourse} contract survive on the object
 *  — the exact key differs by backend version, hence the candidate list. */
function pickField(course: CurrentCourse, keys: string[]): unknown {
  const row = course as unknown as Record<string, unknown>;
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

/** The line under the title — the resume point ("6. Bölüm: …") when the
 *  backend reports one, else the course blurb. */
function resolveSubtitle(course: CurrentCourse): string {
  const resume = pickField(course, [
    'currentSectionTitle',
    'currentSection',
    'lastSectionTitle',
    'lastLessonTitle',
    'lastLessonName',
    'currentContentTitle',
  ]);
  if (typeof resume === 'string') return resume;
  return course.courseDescription ?? '';
}

/** Read the first numeric key off a raw course row. */
function pickNumber(course: CurrentCourse, keys: string[]): number | null {
  const value = pickField(course, keys);
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  // Some backend rows send durations as numeric strings.
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

/** Convert a raw duration to minutes. The current-course rows send
 *  seconds, which is the block default; the other units are there for
 *  hosts on a different backend build. {@code 'auto'} guesses from the
 *  magnitude — a course longer than ~16h in minutes is implausible, so a
 *  big number means a finer unit. */
export function toMinutes(value: number, unit: DurationUnit): number {
  switch (unit) {
    case 'minutes':
      return value;
    case 'seconds':
      return value / 60;
    case 'milliseconds':
      return value / 60_000;
    default:
      if (value >= 100_000) return value / 60_000;
      if (value >= 1_000) return value / 60;
      return value;
  }
}

/**
 * Minutes left in the course, for the "Kalan Süre: 45dk" line.
 *
 * <p>Order of preference:
 * <ol>
 *   <li>An explicit remaining-duration field, when the backend sends one.</li>
 *   <li>{@code courseDuration - attemptDuration} — the course length minus
 *       the time already spent in the player. Both ride along on the
 *       {@code /v1/employee/course/current/search} row.</li>
 *   <li>The course length scaled by the completion percentage, when there's
 *       no attempt time to subtract.</li>
 * </ol>
 *
 * <p>Returns {@code null} when the row carries no duration at all, or when
 * nothing is left — the footer then drops the line instead of showing a
 * made-up or zero value.
 */
export function resolveRemainingMinutes(
  course: CurrentCourse,
  progress: number,
  unit: DurationUnit,
): number | null {
  const explicit = pickNumber(course, [
    'remainingDuration',
    'remainingTime',
    'remainingMinutes',
    'remainingDurationInMinutes',
  ]);
  if (explicit !== null && explicit > 0) return Math.round(toMinutes(explicit, unit));

  const total = pickNumber(course, [
    'courseDuration',
    'totalDuration',
    'duration',
    'durationInMinutes',
  ]);
  if (total === null || total <= 0) return null;
  const totalMinutes = toMinutes(total, unit);

  const spent = pickNumber(course, ['attemptDuration', 'spentDuration', 'timeSpent']);
  if (spent !== null && spent >= 0) {
    let spentMinutes = toMinutes(spent, unit);
    // Auto mode only: the two fields don't have to share a unit (course
    // length in minutes, player attempt time in seconds is a common
    // pairing). Time spent above the course length means the guess read
    // the attempt too coarsely — step down until it fits.
    if (unit === 'auto' && spentMinutes > totalMinutes) {
      const finer = [spent / 60, spent / 60_000].find((v) => v <= totalMinutes);
      if (finer !== undefined) spentMinutes = finer;
    }
    const left = Math.round(totalMinutes - spentMinutes);
    return left > 0 ? left : null;
  }

  const left = Math.round(totalMinutes * (1 - progress / 100));
  return left > 0 ? left : null;
}

/**
 * True when the learner has watched all the course content but still owes
 * the post-test — the backend reports that as
 * {@code attendeeStatus === 'ATTENDED_COURSE'} + {@code pendingPostTest}
 * + a full {@code completionPercentage}. The footer then prompts for the
 * test instead of showing a remaining time (there is none left to spend
 * in the player).
 *
 * <p>All three conditions must hold: a course can be at 100% with the
 * test already taken, and a pending test on a half-finished course is
 * just the normal "keep watching" state.
 */
export function isPostTestPending(course: CurrentCourse, progress: number): boolean {
  const status = pickField(course, ['attendeeStatus']);
  if (typeof status !== 'string' || status.toUpperCase() !== 'ATTENDED_COURSE') return false;

  // Some backend rows send the flag as the string "true".
  const pending = pickField(course, ['pendingPostTest', 'hasPendingPostTest', 'postTestPending']);
  if (pending !== true && pending !== 'true') return false;

  return Math.round(progress) >= 100;
}

/** 45 → "45dk", 75 → "1sa 15dk". */
function formatMinutes(minutes: number, hourSuffix: string, minuteSuffix: string): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}${hourSuffix} ${m}${minuteSuffix}`;
  if (h > 0) return `${h}${hourSuffix}`;
  return `${m}${minuteSuffix}`;
}

export function LastCourse(props: LastCourseProps & Record<string, unknown>) {
  const {
    title: titleProp,
    continueLabel: continueLabelProp,
    courseHrefTemplate = '/lxp/course/{slug}-{courseId}',
    seeAllLabel: seeAllLabelProp,
    seeAllHref,
    showButton = true,
    linkMode = 'full',
    hideWhenEmpty = true,
    columns,
    style,
    paddingOuter,
    headingLine,
    paddingInner,
    titleFontSize = 'xl',
    cardTheme = 'auto',
    showRemainingTime = true,
    durationUnit = 'seconds',
    postTestLabel: postTestLabelProp,
  } = props;
  const { t } = useTranslation('common');
  const title = titleProp || t('title.currentLearnings');
  const continueLabel = continueLabelProp || t('button.continue', { defaultValue: 'Devam Et' });
  const seeAllLabel = seeAllLabelProp || t('button.seeAll');
  const remainingLabel = t('label.remainingTime', { defaultValue: 'Kalan Süre' });
  const postTestLabel =
    postTestLabelProp ||
    t('label.postTestRequired', { defaultValue: 'Son Testi Çözmeniz Gerekmektedir' });
  const hourSuffix = t('label.hourShort', { defaultValue: 'sa' });
  const minuteSuffix = t('label.minuteShort', { defaultValue: 'dk' });
  // 'auto' means "no override" — the card then reads the host's design
  // tokens and flips with the portal's own light/dark theme.
  const cardThemeAttr = cardTheme === 'auto' ? undefined : cardTheme;
  const isButtonLinkMode = linkMode === 'button';
  // In button-link mode the card itself isn't clickable, so the button
  // is the only way to reach the course — always show it regardless of
  // the admin's "Show button" toggle.
  const resolvedShowButton = isButtonLinkMode ? true : showButton;
  const user = useCurrentUser();
  const learning = useLearningSource();

  // Read the course count from `columns` or any of the likely alias keys
  // the backend "Number of courses" field might use, so the block honors
  // the admin value regardless of the exact schema key.
  const rawCount =
    columns ??
    props.count ??
    props.courseCount ??
    props.numberOfCourses ??
    props.numCourses ??
    props.maxCourses ??
    props.items;
  const cols = resolveColumns(rawCount as LastCourseProps['columns']);

  const state = useAsyncResource<CurrentCourse[]>(
    async (signal) => {
      if (!user) return null;
      if (!learning?.fetchCurrentCourse) return null;
      const items = await learning.fetchCurrentCourse({ limit: Math.max(cols, 5), signal });
      // return items;
      return items && items.length > 0 ? items.slice(0, cols) : null;
    },
    [user, learning, cols],
  );

  // Sadece login olan kullanıcıya göster
  if (!user) return null;

  // Adapter bağlı değil
  if (!learning?.fetchCurrentCourse) {
    return (
      <SectionShell
        headingLine={headingLine}
        style={style}
        title={title}
        titleFontSize={titleFontSize}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        <AdapterPlaceholder
          blockType="LastCourse"
          adapterName="LearningSource.fetchCurrentCourse"
        />
      </SectionShell>
    );
  }

  const seeAllAction = seeAllHref ? (
    <PortalLink
      href={seeAllHref}
      style={{
        color: 'var(--color-brand-600, #d6007f)',
        fontWeight: 500,
        textDecoration: 'none',
        fontSize: 'var(--font-size-sm, 14px)',
      }}
    >
      {seeAllLabel} →
    </PortalLink>
  ) : undefined;

  const cardStyles = <LastCourseCardStyles />;

  const gridStyle = { '--lc-cols': cols } as CSSProperties;
  // Desktop: 1 = single big card, 2 = horizontal cards in a row. The 2-up row
  // (`--row`) collapses to one stacked column on tablet/phone — the cards stay
  // horizontal, they just no longer share a row.
  const gridClassName = `last-course-grid${cols > 1 ? ' last-course-grid--row' : ''}`;

  // Loading
  if (state.kind === 'loading') {
    return (
      <SectionShell
        headingLine={headingLine}
        style={style}
        title={title}
        titleFontSize={titleFontSize}
        action={seeAllAction}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        {cardStyles}
        <div className={gridClassName} style={gridStyle}>
          {Array.from({ length: cols }).map((_, ci) => (
            <LastCourseCardSkeleton key={ci} cardTheme={cardThemeAttr} />
          ))}
        </div>
      </SectionShell>
    );
  }

  // Empty
  if (state.kind === 'empty' && hideWhenEmpty) return null;

  // Empty / Error
  if (state.kind !== 'loaded') {
    return (
      <SectionShell
        headingLine={headingLine}
        style={style}
        title={title}
        titleFontSize={titleFontSize}
        action={seeAllAction}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        <EmptyState
          title={t('site-builder-blocks:lastCourse.empty.title')}
          hint={t('site-builder-blocks:lastCourse.empty.hint')}
        />
      </SectionShell>
    );
  }

  const courses = state.data;

  return (
    <SectionShell
      headingLine={headingLine}
      style={style}
      title={title}
      titleFontSize={titleFontSize}
      action={seeAllAction}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      {cardStyles}
      <div
        className={gridClassName}
        style={gridStyle}
        data-lc-cols={cols}
        data-lc-count={courses.length}
      >
        {courses.map((course) => {
          const slug = slugifyTitle(course.courseTitle) || 'course';
          const href = courseHrefTemplate
            .replace('{slug}', encodeURIComponent(slug))
            .replace('{courseId}', encodeURIComponent(String(course.courseId)));
          const progress = Math.min(100, Math.max(0, course.completionPercentage ?? 0));
          // "6. Bölüm: …" — the resume point when the backend sends one,
          // otherwise the course blurb.
          const subtitle = resolveSubtitle(course);
          // Content done, post-test still owed: the footer prompts for the
          // test instead of a remaining time (there is none left to spend).
          const postTestPending = isPostTestPending(course, progress);
          const remaining =
            showRemainingTime && !postTestPending
              ? resolveRemainingMinutes(course, progress, durationUnit)
              : null;

          return (
            <LastCourseCard
              key={course.id}
              href={href}
              title={course.courseTitle ?? ''}
              subtitle={subtitle}
              thumbnail={course.courseThumbnail}
              progress={progress}
              meta={
                postTestPending
                  ? postTestLabel
                  : remaining !== null
                    ? `${remainingLabel}: ${formatMinutes(remaining, hourSuffix, minuteSuffix)}`
                    : undefined
              }
              ctaLabel={continueLabel}
              showCta={resolvedShowButton}
              linkMode={linkMode}
              cardTheme={cardThemeAttr}
            />
          );
        })}
      </div>
    </SectionShell>
  );
}
